import { useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import {
  Box,
  Button,
  Dialog,
  H1,
  HStack,
  Icon,
  type IconName,
  ListRow,
  Separator,
  ShimmerText,
  Text,
  VStack,
} from '@betterment/samba-react';

import { ErrorBoundary } from '~/b4b/components/error-boundary/ErrorBoundary';
import { HtmlContent } from '~/b4b/components/html-content/HtmlContent';
import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { RoundedBorderBox } from '~/b4b/features/plan-benchmark/components/RoundedBorderBox';
import { urlBuilder } from '~/b4b/utils/urlBuilder';
import { PlanBenchmarkResourceKey } from '~/graphql/schema';

import AiOrb from './assets/icon_ai_orb.svg';
import {
  GeneratePlanBenchmarkDocument,
  type GeneratePlanBenchmarkMutation,
} from './graphql/mutation.generatePlanBenchmark.generated';
import { PlanBenchmarkButton } from './PlanBenchmarkButton';

const resourceConfig: Record<
  PlanBenchmarkResourceKey,
  { label: string; href: string; trackingName: string; icon: IconName }
> = {
  [PlanBenchmarkResourceKey.Participation]: {
    label: 'Read more about employee participation',
    href: urlBuilder.resourcesTipsForIncreasingParticipation(),
    trackingName: 'BenchmarkDialogParticipationResource',
    icon: 'DocumentPopulated',
  },
  [PlanBenchmarkResourceKey.Contribution]: {
    label: 'How to pick a 401(k) contribution rate',
    href: urlBuilder.resourcesContributionRateGuide(),
    trackingName: 'BenchmarkDialogContributionResource',
    icon: 'DocumentPopulated',
  },
  [PlanBenchmarkResourceKey.ContributionReport]: {
    label: 'Run current contribution rates report',
    href: urlBuilder.reportsNew({ report: 'current_deferral_rates' }),
    trackingName: 'BenchmarkDialogDefReport',
    icon: 'Bar',
  },
};

type Props = { planId: string };
type BenchmarkResult = NonNullable<GeneratePlanBenchmarkMutation['generatePlanBenchmark']['result']>;
type RateMetric = BenchmarkResult['participationRate'];

const formatOneDecimal = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
};

const formatRate = (rate: number) => `${formatOneDecimal(rate)}%`;

const BenchmarkTableHeader = () => (
  <HStack css={{ gap: 'space.200', alignItems: 'center', width: '100%' }}>
    <H1 variant="title100" css={{ flex: 1 }}>
      Benchmark details
    </H1>
    <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
      Your plan
    </Text>
    <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
      National Avg.*
    </Text>
  </HStack>
);

const BenchmarkDisclosure = ({ content }: { content: DocumentFragment }) => (
  <Box css={{ fontSize: 'fontSize.25', color: 'text.weak', '& a': { color: 'inherit', textDecoration: 'underline' } }}>
    <HtmlContent documentFragment={content} />
  </Box>
);

const AIDisclaimer = () => (
  <Text variant="text25" color="text.weak">
    AI summary based on your plan's participation and contribution data; accuracy is not guaranteed. We never use your
    data to train our models. See{' '}
    <SambaAutoLink
      to="https://www.betterment.com/important-betterment-disclosures"
      css={{ color: 'inherit', textDecoration: 'underline' }}
    >
      important disclosures
    </SambaAutoLink>{' '}
    and our{' '}
    <SambaAutoLink
      to="https://www.betterment.com/legal/privacy-policy"
      css={{ color: 'inherit', textDecoration: 'underline' }}
    >
      privacy policy
    </SambaAutoLink>
    .
  </Text>
);

const BenchmarkSkeletonRow = () => (
  <HStack css={{ gap: 'space.200', alignItems: 'center', width: '100%' }}>
    <Box css={{ flex: 1 }}>
      <ShimmerText textStyle="text100" css={{ width: '149px' }} />
    </Box>
    <Box css={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
      <ShimmerText textStyle="text100" css={{ width: '56px' }} />
    </Box>
    <Box css={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
      <ShimmerText textStyle="text100" css={{ width: '45px' }} />
    </Box>
  </HStack>
);

const BenchmarkSkeleton = () => (
  <VStack css={{ gap: 'space.300', width: '100%', pt: 'space.400' }}>
    <VStack css={{ gap: 'space.200', width: '100%' }}>
      <BenchmarkTableHeader />
      <Separator />
      <BenchmarkSkeletonRow />
      <Separator />
      <BenchmarkSkeletonRow />
      <Separator />
      <ShimmerText textStyle="text25" css={{ width: '70%' }} />
    </VStack>
    <RoundedBorderBox borderStyle="gradientRotateOnce" width="size.full">
      <VStack css={{ padding: 'space.300', gap: 'space.200', width: '100%' }}>
        <HStack css={{ gap: 'space.50', alignItems: 'center', width: '100%' }}>
          <img src={AiOrb} alt="" width={24} height={24} />
          <ShimmerText textStyle="text100" css={{ width: '131px' }} />
        </HStack>
        <VStack css={{ gap: 'space.150' }}>
          <VStack css={{ gap: 'space.100' }}>
            <ShimmerText textStyle="text25" css={{ width: '100%' }} />
            <ShimmerText textStyle="text25" css={{ width: '90%' }} />
            <ShimmerText textStyle="text25" css={{ width: '80%' }} />
          </VStack>
          <VStack css={{ gap: 'space.100' }}>
            <ShimmerText textStyle="text25" css={{ width: '94%' }} />
            <ShimmerText textStyle="text25" css={{ width: '100%' }} />
            <ShimmerText textStyle="text25" css={{ width: '90%' }} />
          </VStack>
        </VStack>
      </VStack>
    </RoundedBorderBox>
  </VStack>
);

const BenchmarkDetailsRow = ({ label, planValue, benchmarkValue }: RateMetric) => (
  <HStack css={{ gap: 'space.200', alignItems: 'center', width: '100%' }}>
    <Text variant="text100" css={{ flex: 1 }}>
      {label}
    </Text>
    <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
      {formatRate(planValue)}
    </Text>
    <Text variant="text100" css={{ flex: 1, textAlign: 'right' }}>
      {formatRate(benchmarkValue)}
    </Text>
  </HStack>
);

const BenchmarkDetails = ({
  benchmarkDisclosure,
  participationRate,
  contributionRate,
}: Pick<BenchmarkResult, 'participationRate' | 'contributionRate' | 'benchmarkDisclosure'>) => (
  <VStack css={{ gap: 'space.200', width: '100%' }}>
    <BenchmarkTableHeader />
    <Separator />
    <BenchmarkDetailsRow {...participationRate} />
    <Separator />
    <BenchmarkDetailsRow {...contributionRate} />
    <Separator />
    <BenchmarkDisclosure content={benchmarkDisclosure} />
  </VStack>
);

const ResourceRow = ({
  href,
  icon,
  label,
  trackingName,
}: {
  href: string;
  icon: IconName;
  label: string;
  trackingName: string;
}) => (
  <SambaAutoLink
    to={href}
    target="_blank"
    rel="noopener noreferrer"
    css={{ color: 'inherit', textDecoration: 'none', width: '100%' }}
    data-track-event="ElementClicked"
    data-track-name={trackingName}
    data-track-module="Insights"
    data-track-flow="HomeLanding"
  >
    <ListRow css={{ padding: 'space.0', background: 'transparent' }}>
      <ListRow.LeadingAsset>
        <Icon name={icon} size="small" color="icon" />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text variant="text50">{label}</Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingAsset>
        <Icon name="CaretRight" size="xsmall" color="icon" />
      </ListRow.TrailingAsset>
    </ListRow>
  </SambaAutoLink>
);

const AISummary = ({ aiSummary, suggestedResources }: Pick<BenchmarkResult, 'aiSummary' | 'suggestedResources'>) => (
  <RoundedBorderBox borderStyle="gradientRotateOnce" width="size.full">
    <VStack css={{ padding: 'space.300', gap: 'space.300', width: '100%' }}>
      <VStack css={{ gap: 'space.200', width: '100%' }}>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <img src={AiOrb} alt="" width={24} height={24} />
          <H1 variant="title100">{aiSummary.title}</H1>
        </HStack>

        <Box css={{ fontSize: 'fontSize.100' }}>
          <HtmlContent documentFragment={aiSummary.body} />
        </Box>
      </VStack>
      <VStack css={{ gap: 'space.200', width: '100%' }}>
        {suggestedResources
          .filter(resource => resource.key in resourceConfig)
          .map(resource => {
            const config = resourceConfig[resource.key];
            return (
              <ResourceRow
                key={resource.key}
                href={config.href}
                icon={config.icon}
                label={config.label}
                trackingName={config.trackingName}
              />
            );
          })}
      </VStack>

      <VStack css={{ gap: 'space.200', width: '100%' }}>
        <Separator />
        <AIDisclaimer />
      </VStack>
    </VStack>
  </RoundedBorderBox>
);

const BenchmarkContent = ({
  suggestedResources,
  benchmarkDisclosure,
  participationRate,
  contributionRate,
  aiSummary,
}: BenchmarkResult) => (
  <VStack css={{ gap: 'space.300', width: '100%', pt: 'space.400' }}>
    <BenchmarkDetails
      benchmarkDisclosure={benchmarkDisclosure}
      participationRate={participationRate}
      contributionRate={contributionRate}
    />
    <AISummary aiSummary={aiSummary} suggestedResources={suggestedResources} />
  </VStack>
);

const BenchmarkError = ({ onRetry }: { onRetry: () => void }) => (
  <VStack
    css={{
      gap: 'space.200',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'space.400',
      maxWidth: '400px',
      margin: '0 auto',
    }}
  >
    <Icon name="ExclamationCircle" size="medium" color="icon" />
    <Text variant="text100" color="text" css={{ textAlign: 'center' }}>
      We were unable to generate your plan benchmark. Please try again.
    </Text>
    <Button
      variant="solidPrimary"
      onClick={onRetry}
      data-track-event="ElementClicked"
      data-track-name="AIBenchmarkRetry"
      data-track-module="Insights"
      data-track-flow="HomeLanding"
    >
      Try again
    </Button>
  </VStack>
);

export const PlanBenchmarkWidget: React.FC<Props> = ({ planId }) => {
  const [generate, { data, loading, error }] = useGraphQLMutation(GeneratePlanBenchmarkDocument);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    if (newOpen && !data && !loading) {
      void generate({ variables: { input: { planId } } });
    }
  };

  const result = data?.generatePlanBenchmark.result;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild={true}>
        <PlanBenchmarkButton />
      </Dialog.Trigger>
      <Dialog.Content
        size="large"
        aria-describedby={undefined}
        data-track-event="ElementViewed"
        data-track-name="AIBenchmarkDialog"
        data-track-module="Insights"
        data-track-flow="HomeLanding"
        css={{ p: 'space.400', minWidth: '400px', maxWidth: '640px' }}
      >
        <Dialog.Title asChild={true}>
          <H1 variant="title300">Plan benchmark</H1>
        </Dialog.Title>
        <ErrorBoundary name="plan-benchmark">
          {loading && <BenchmarkSkeleton />}
          {error && !loading && <BenchmarkError onRetry={() => void generate({ variables: { input: { planId } } })} />}
          {result && <BenchmarkContent {...result} />}
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
